import React, { useEffect } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { AlignItems, Box, Display, FlexDirection } from '@gousto-internal/citrus-react'

import type { MenuPreferences } from '@library/api-menu-experience'
import {
  CategoryBranding,
  CollectionDescription,
  CollectionTitle,
  RecipeCount,
} from '@library/menu-page'

import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useMerchandisingHeader } from 'routes/Menu/MenuRecipesPage/CollectionHeader'
import { useCollectionHeaders } from 'routes/Menu/MenuRecipesPage/CollectionHeader/CollectionHeaderWrapper/hooks'
import { useShouldRemoveMerchandisingBanner } from 'routes/Menu/MenuRecipesPage/CollectionHeader/useShouldRemoveMerchandisingBanner'
import { useBasketDelivery } from 'routes/Menu/domains/basket/internal/delivery'
import { useBrandHeadersInfo } from 'routes/Menu/domains/brand'
import { CollectionId } from 'routes/Menu/domains/collections'

import { CollectionHeader } from './CollectionHeader/CollectionHeader'
import { FeaturedRangeCollectionHeader } from './CollectionHeader/FeaturedRangeCollectionHeader'
import { RecipeGrid } from './RecipeGrid/RecipeGrid'
import { getOldCollectionMerchandisingHeader } from './getOldCollectionMerchandisingHeader'

type CollectionScreenProps = {
  menuPreferences?: MenuPreferences
  userStatus?: string
  isFlexOrder?: boolean
  isFeaturedRangeMerchandisingEnabled?: boolean
}

const CATEGORY_DESCRIPTION: Record<string, { title: string; subtitle: string }> = {
  default: {
    title: "Sorted by what's relevant to you.",
    subtitle: "Based on what you've had before.",
  },
  [CollectionId.Recommendations]: {
    title: "Based on what you've had before.",
    subtitle: "We think you'll love these recipes.",
  },
}

export const CollectionScreen = ({
  menuPreferences,
  userStatus = '',
  isFlexOrder = false,
  isFeaturedRangeMerchandisingEnabled,
}: CollectionScreenProps) => {
  const currentCollectionId = useCurrentCollectionId()

  const collection = useCollection(currentCollectionId)
  const { shortTitle, recipes } = collection ?? {
    shortTitle: '',
    recipes: [],
  }

  const delivery = useBasketDelivery()

  // prev menu-headers endpoint based headers
  const {
    headers,
    collectionsPerMenu,
    isLoading: isBrandHeadersLoading,
  } = useBrandHeadersInfo(isFeaturedRangeMerchandisingEnabled)
  const brandMerchandisingHeader = useCollectionHeaders(headers, collectionsPerMenu)

  // next menuv3/experience-api based headers
  const featuredRangeMerchandisingHeader = useMerchandisingHeader(currentCollectionId)

  // old headers based on either prev or next data
  const oldMerchandisingHeader = isFeaturedRangeMerchandisingEnabled
    ? getOldCollectionMerchandisingHeader(collection?.merchandising)
    : brandMerchandisingHeader

  const removeMerchandisingBanner = useShouldRemoveMerchandisingBanner()
  const showOldMerchandisingBanner = isFeaturedRangeMerchandisingEnabled
    ? featuredRangeMerchandisingHeader?.type === 'wave-link-header' ||
      featuredRangeMerchandisingHeader?.type === 'gradient-info-header'
    : Boolean(oldMerchandisingHeader) && !removeMerchandisingBanner

  const showAccountOnHoldAlert = userStatus === 'onhold'

  const { title, subtitle } =
    CATEGORY_DESCRIPTION[currentCollectionId] ?? CATEGORY_DESCRIPTION.default

  const isMerchandisingHeadersLoading = isFeaturedRangeMerchandisingEnabled
    ? !collection
    : isBrandHeadersLoading
  const merchandisingBannerShown =
    showOldMerchandisingBanner && !isFlexOrder && !showAccountOnHoldAlert

  useEffect(() => {
    if (isMerchandisingHeadersLoading) {
      return
    }

    datadogRum?.addAction('view_collection_screen', {
      deliveryDate: delivery.date,
      collectionId: currentCollectionId,
      recipeCount: recipes.length,
      merchandisingBannerShown,
    })
  }, [
    currentCollectionId,
    recipes.length,
    merchandisingBannerShown,
    delivery.date,
    isMerchandisingHeadersLoading,
  ])

  const categoryBranding =
    featuredRangeMerchandisingHeader?.type === 'category-branding'
      ? featuredRangeMerchandisingHeader
      : null

  return (
    <>
      {isFeaturedRangeMerchandisingEnabled ? (
        <FeaturedRangeCollectionHeader
          showAccountOnHoldAlert={showAccountOnHoldAlert}
          isFlexOrder={isFlexOrder}
          merchandisingHeader={featuredRangeMerchandisingHeader}
        />
      ) : (
        <CollectionHeader
          showAccountOnHoldAlert={showAccountOnHoldAlert}
          isFlexOrder={isFlexOrder}
          showMerchandisingBanner={showOldMerchandisingBanner}
          merchandisingHeader={oldMerchandisingHeader}
        />
      )}
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
      >
        {isFeaturedRangeMerchandisingEnabled ? (
          categoryBranding ? (
            // we display branding if available
            // branding replaces category title and description
            <CategoryBranding contentBlock={categoryBranding} recipeCount={recipes.length}>
              <RecipeGrid menuPreferences={menuPreferences} />
            </CategoryBranding>
          ) : (
            // If there is no branding, then we continue displaying old headers
            <Box maxWidth="1600px" width="100%">
              <Box paddingH={[3, 3, 0]} paddingBottom={2}>
                {!merchandisingBannerShown && <CollectionTitle text={shortTitle} />}
                <CollectionDescription title={title} subtitle={subtitle} />
                <RecipeCount count={recipes.length} />
              </Box>
              <RecipeGrid menuPreferences={menuPreferences} />
            </Box>
          )
        ) : (
          // pre-featured range merchandising
          <Box maxWidth="1600px" width="100%">
            <Box paddingH={[3, 3, 0]} paddingBottom={2}>
              {!merchandisingBannerShown && <CollectionTitle text={shortTitle} />}
              <CollectionDescription title={title} subtitle={subtitle} />
              <RecipeCount count={recipes.length} />
            </Box>
            <RecipeGrid menuPreferences={menuPreferences} />
          </Box>
        )}
      </Box>
    </>
  )
}
